<template>
  <!-- A native dialog: Escape closes it, the keyboard stays inside, the page behind is dimmed -->
  <dialog
    ref="dialog"
    :class="$style['preview']"
    aria-labelledby="bag-preview-title"
    @close="onClosed"
    @cancel="exit.onCancel"
    @click="onClick">
    <div :class="$style['preview__head']">
      <h2 id="bag-preview-title" :class="$style['preview__title']">
        Mijn reservering
        <span v-if="itemCount" :class="$style['preview__count']">{{ itemCount }} {{ itemCount === 1 ? 'item' : 'items' }}</span>
      </h2>
      <Button variant="ghost" icon="X" type="button" autofocus @click="hide">Sluiten</Button>
    </div>

    <div v-if="items.length" :class="$style['preview__items']">
      <ShoppingBagItem
        v-for="item in items"
        :key="item.id"
        :item="item"
        @remove="removeItem(item.id)"
        @update-quantity="qty => updateQuantity(item.id, qty)" />
    </div>

    <!-- Nothing chosen yet: the way to the collection -->
    <div v-else :class="$style['preview__empty']">
      <p :class="$style['preview__empty-title']">Je hebt nog niets gekozen</p>
      <p>Zoek in het assortiment wat je wilt huren en voeg het toe. Daarna vraag je hier alles in één keer aan.</p>
    </div>

    <div :class="$style['preview__foot']">
      <Button v-if="items.length" variant="primary" :to="{ name: 'kleding-reserveren' }" full-width>
        Reservering aanvragen
      </Button>
      <Button v-else variant="primary" :to="{ name: 'kleding' }" full-width>
        Bekijk het assortiment
      </Button>
      <Button v-if="items.length" variant="ghost" type="button" full-width @click="hide">
        Verder kijken
      </Button>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { useCssModule } from 'vue'

// What is in the reservation, over the page: check it, change it, then on to sending it.
// Larger screens: a panel from the right. Phones: a sheet from the bottom, like the filters.
const { open, hide } = useBagPreview()
const { items, itemCount, removeItem, updateQuantity } = useShoppingBag()
const route = useRoute()

const dialog = ref<HTMLDialogElement>()
const style = useCssModule()
// It leaves the way it came in
const exit = useDialogExit(dialog, style['preview--closing']!)
let returnFocus: HTMLElement | null = null
let overflow = ''

watch(open, isOpen => {
  const element = dialog.value
  if (!element) { return }
  if (isOpen && !element.open) {
    returnFocus = document.activeElement as HTMLElement | null
    // The page behind stays where it is
    overflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = 'hidden'
    element.showModal()
  } else if (!isOpen && element.open) {
    exit.close()
  }
})

// Once it is really closed (after the way out, or at once on Escape twice)
function onClosed () {
  hide()
  document.documentElement.style.overflow = overflow
  // Safari does not give the focus back by itself; after a link the new page has it
  if (returnFocus?.isConnected) { returnFocus.focus({ preventScroll: true }) }
  returnFocus = null
}

// A click on the dimmed page around it closes it
const onClick = (event: MouseEvent) => {
  if (event.target === dialog.value) { hide() }
}

// Following a link in it (an item, or on to the request) closes it; the new page keeps the focus
watch(() => route.fullPath, () => {
  returnFocus = null
  hide()
})

onBeforeUnmount(() => {
  hide()
  document.documentElement.style.overflow = overflow
})
</script>

<style module>
/* A panel from the right, the height of the screen; the global margin reset would pin it to the left */
.preview {
  position: fixed;
  inset: 0 0 0 auto;
  width: min(28rem, 100%);
  max-width: none;
  height: 100%;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  border-left: var(--stage-hairline) solid var(--stage-line);
  background: var(--stage-black);
  color: var(--text-base);
}

.preview[open] {
  display: flex;
  flex-direction: column;
  animation: preview-in 350ms var(--stage-ease);
}

.preview[open]::backdrop {
  background: oklch(0% 0 0deg / 70%);
  animation: preview-backdrop-in 300ms ease-out;
}

@keyframes preview-in {
  from {
    transform: translateX(100%);
  }
}

@keyframes preview-backdrop-in {
  from {
    opacity: 0;
  }
}

/* The way out: back to where it came from */
.preview--closing[open] {
  animation: preview-out 300ms var(--stage-ease) forwards;
}

.preview--closing[open]::backdrop {
  animation: preview-backdrop-out 300ms ease-in forwards;
}

@keyframes preview-out {
  to {
    transform: translateX(100%);
  }
}

@keyframes preview-backdrop-out {
  to {
    opacity: 0;
  }
}

.preview__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-m);
  padding: var(--spacing-l) var(--spacing-l) var(--spacing-l) var(--spacing-xl);
  border-bottom: var(--stage-hairline) solid var(--stage-line-soft);
}

/* Bold but in sentence case: the page keeps its one title in capitals */
.preview__title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 var(--spacing-m);
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-large);
  line-height: 1.2;
  letter-spacing: 0;
  text-transform: none;
}

.preview__count {
  color: var(--text-subtle);
  font-weight: var(--font-weight-normal);
  font-size: var(--font-size-small);
}

/* The items scroll; the buttons stay at the bottom. The items lay themselves out by this width. */
.preview__items {
  flex: 1;
  padding: 0 var(--spacing-xl);
  overflow-y: auto;
  overscroll-behavior: contain;
  container-type: inline-size;
}

.preview__items > :first-child {
  border-top: 0;
}

.preview__empty {
  flex: 1;
  padding: var(--spacing-xl);
  color: var(--text-subtle);
}

.preview__empty p {
  margin: 0 0 var(--spacing-m);
}

.preview__empty .preview__empty-title {
  color: var(--text-base);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-large);
}

.preview__foot {
  display: flex;
  flex-direction: column;
  gap: var(--spacing);
  padding: var(--spacing-l) var(--spacing-xl) calc(var(--spacing-l) + env(safe-area-inset-bottom, 0rem));
  border-top: var(--stage-hairline) solid var(--stage-line-soft);
}

/* Phones: a sheet from the bottom, where the thumb is */
@media (width <= 40rem) {
  .preview {
    inset: auto 0 0;
    width: 100%;
    height: auto;
    max-height: 85svh;
    border-top: var(--stage-hairline) solid var(--stage-line);
    border-left: 0;
  }

  .preview[open] {
    animation-name: preview-up;
  }

  .preview--closing[open] {
    animation-name: preview-down;
  }

  @keyframes preview-up {
    from {
      transform: translateY(100%);
    }
  }

  @keyframes preview-down {
    to {
      transform: translateY(100%);
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .preview[open],
  .preview[open]::backdrop {
    animation: none;
  }
}
</style>
